// Conversant's managed WAF protection policies — a fixed vendor catalog shared
// by the General Protection page (full table + rule-group detail) and the WAAP
// onboarding wizard (policy picker help text). Single source of truth so the
// two surfaces never drift on labels or descriptions.
//
// `id` is the vendor `strategy_type` the `waf_strategies` / `mapping` feeds key
// domains by. Confirmed against live data: 1 = Basic, 3 = Strict. The remaining
// ids follow the portal's display order and are best-effort (to-confirm) — they
// only affect the per-policy "Website" join, which is empty for unused policies.

export interface WafPolicy {
  id: number;
  label: string;
  description: string;
  /** Fixed vendor Rule Group list (FE-only; the portal has no API for it). */
  ruleGroups: string[];
}

export const WAF_POLICIES: WafPolicy[] = [
  {
    id: 1,
    label: 'Basic',
    description:
      'Basic policy consists of rules labelled as Basic, providing basic-level security with their actions all configured to Block. It represents a relatively lower ability in attack detection and thus a lower possibility of causing false positives.',
    ruleGroups: [
      'XSS Protection Group',
      'SQL Injection Protection Group',
      'Command Injection Protection Group',
      'Scanners & Crawlers Protection Group',
      'XSS Injection Semantics Protection Group',
      'SQL Injection Semantics Protection Group',
      'Command Injection Semantics Protection Group',
    ],
  },
  {
    id: 2,
    label: 'Standard',
    description:
      'Standard policy consists of rules labelled respectively as Basic and Standard, providing average-level security with their actions all configured to Block. It represents an average-level ability in attack detection and thus an average possibility of causing false positives.',
    ruleGroups: [
      'XSS Protection Group',
      'SQL Injection Protection Group',
      'Command Injection Protection Group',
      'Scanners & Crawlers Protection Group',
      'Illegal File Upload Protection',
      'PHP Injection Protection',
      'XML Injection Protection',
      'JAVA Deserialization Protection',
      'JAVA Code Injection Protection',
      'HTTP Protocol Protection',
      'Sensitive File Sniffing Protection',
      'Local File Inclusion Protection',
      'Remote File Inclusion Protection',
      'Server Vulnerability Protection',
      'XSS Injection Semantics Protection Group',
      'JAVA OGNL Semantics Protection',
      'SQL Injection Semantics Protection Group',
      'Command Injection Semantics Protection Group',
      'Local File Inclusion Semantics Protection Group',
      'Remote File Inclusion Semantics Protection Group',
    ],
  },
  {
    id: 3,
    label: 'Strict',
    description:
      'Strict policy consists of rules labelled as Basic, Standard, and Strict, providing high-level security with their actions all configured to Block. It represents a relatively higher ability in attack detection and thus a higher possibility of causing false positives.',
    ruleGroups: [
      'XSS Protection Group',
      'SQL Injection Protection Group',
      'Command Injection Protection Group',
      'Scanners & Crawlers Protection Group',
      'Illegal File Upload Protection',
      'PHP Injection Protection',
      'XML Injection Protection',
      'JAVA Deserialization Protection',
      'JAVA Code Injection Protection',
      'HTTP Protocol Protection',
      'Local File Inclusion Protection',
      'Remote File Inclusion Protection',
      'Session Fixation Protection',
      'Server Vulnerability Protection',
      'Sensitive File Sniffing Protection',
      'XSS Injection Semantics Protection Group',
      'JAVA OGNL Semantics Protection',
      'SQL Injection Semantics Protection Group',
      'Command Injection Semantics Protection Group',
      'Local File Inclusion Semantics Protection Group',
      'Remote File Inclusion Semantics Protection Group',
    ],
  },
  {
    id: 4,
    label: 'Basic Plus',
    description:
      'Basic Plus policy consists of rules labelled separately as Basic, Standard and Strict, with actions of Basic rules configured to Block while the rest configured to Monitor.',
    ruleGroups: [
      'XSS Protection Group',
      'SQL Injection Protection Group',
      'Command Injection Protection Group',
      'Scanners & Crawlers Protection Group',
      'Illegal File Upload Protection',
      'PHP Injection Protection',
      'XML Injection Protection',
      'JAVA Deserialization Protection',
      'JAVA Code Injection Protection',
      'HTTP Protocol Protection',
      'Local File Inclusion Protection',
      'Remote File Inclusion Protection',
      'Session Fixation Protection',
      'Server Vulnerability Protection',
      'Web Shell Protection',
      'XSS Injection Semantics Protection Group',
      'JAVA OGNL Semantics Protection',
      'SQL Injection Semantics Protection Group',
      'Command Injection Semantics Protection Group',
      'Web Shell Semantics Protection Group',
      'Local File Inclusion Semantics Protection Group',
      'Remote File Inclusion Semantics Protection Group',
    ],
  },
  {
    id: 5,
    label: 'Standard Plus',
    description:
      'Standard Plus policy consists of rules labelled separately as Basic, Standard and Strict, with actions of Basic and Standard rules configured to Block while the rest configured to Monitor.',
    ruleGroups: [
      'XSS Protection Group',
      'SQL Injection Protection Group',
      'Command Injection Protection Group',
      'Scanners & Crawlers Protection Group',
      'Illegal File Upload Protection',
      'PHP Injection Protection',
      'XML Injection Protection',
      'JAVA Deserialization Protection',
      'JAVA Code Injection Protection',
      'HTTP Protocol Protection',
      'Local File Inclusion Protection',
      'Remote File Inclusion Protection',
      'Session Fixation Protection',
      'Server Vulnerability Protection',
      'Web Shell Protection',
      'XSS Injection Semantics Protection Group',
      'JAVA OGNL Semantics Protection',
      'SQL Injection Semantics Protection Group',
      'Command Injection Semantics Protection Group',
      'Web Shell Semantics Protection Group',
      'Local File Inclusion Semantics Protection Group',
      'Remote File Inclusion Semantics Protection Group',
    ],
  },
  {
    id: 6,
    label: 'Monitoring',
    description: 'Monitoring policy consists of rules with actions all configured to Monitor.',
    ruleGroups: [
      'XSS Protection Group',
      'SQL Injection Protection Group',
      'Command Injection Protection Group',
      'Scanners & Crawlers Protection Group',
      'Illegal File Upload Protection',
      'PHP Injection Protection',
      'XML Injection Protection',
      'JAVA Deserialization Protection',
      'JAVA Code Injection Protection',
      'HTTP Protocol Protection',
      'Local File Inclusion Protection',
      'Remote File Inclusion Protection',
      'Session Fixation Protection',
      'Server Vulnerability Protection',
      'Web Shell Semantics Protection',
      'XSS Injection Semantics Protection Group',
      'JAVA OGNL Semantics Protection',
      'SQL Injection Semantics Protection Group',
      'Command Injection Semantics Protection Group',
      'Web Shell Semantics Protection Group',
      'Local File Inclusion Semantics Protection Group',
      'Remote File Inclusion Semantics Protection Group',
    ],
  },
  {
    id: 7,
    label: 'Balanced',
    description:
      'Provides a general-purpose, well-balanced protection layer against common web threats. Suitable for testing and validation scenario with a detection ability set to average level.',
    ruleGroups: [
      'XSS Protection Group',
      'SQL Injection Protection Group',
      'Command Injection Protection Group',
      'Scanners & Crawlers Protection Group',
      'Illegal File Upload Protection',
      'PHP Injection Protection',
      'XML Injection Protection',
      'JAVA Deserialization Protection',
      'JAVA Code Injection Protection',
      'HTTP Protocol Protection',
      'Local File Inclusion Protection',
      'Remote File Inclusion Protection',
      'Session Fixation Protection',
      'Server Vulnerability Protection',
      'Web Shell Semantics Protection',
      'XSS Injection Semantics Protection Group',
      'JAVA OGNL Semantics Protection',
      'SQL Injection Semantics Protection Group',
      'Command Injection Semantics Protection Group',
      'Web Shell Semantics Protection Group',
      'Local File Inclusion Semantics Protection Group',
      'Remote File Inclusion Semantics Protection Group',
    ],
  },
  {
    id: 8,
    label: 'Breach and Attack Simulation (BAS)',
    description:
      "Emulates real-world attack techniques (used in penetration testing and red teaming) to validate your WAF's effectiveness and identify gaps in protection.",
    ruleGroups: [
      'XSS Protection Group',
      'SQL Injection Protection Group',
      'Command Injection Protection Group',
      'Scanners & Crawlers Protection Group',
      'Illegal File Upload Protection',
      'PHP Injection Protection',
      'XML Injection Protection',
      'JAVA Deserialization Protection',
      'JAVA Code Injection Protection',
      'HTTP Protocol Protection',
      'Local File Inclusion Protection',
      'Remote File Inclusion Protection',
      'Session Fixation Protection',
      'Server Vulnerability Protection',
      'Web Shell Semantics Protection',
      'XSS Injection Semantics Protection Group',
      'JAVA OGNL Semantics Protection',
      'SQL Injection Semantics Protection Group',
      'Command Injection Semantics Protection Group',
      'Web Shell Semantics Protection Group',
      'Local File Inclusion Semantics Protection Group',
      'Remote File Inclusion Semantics Protection Group',
    ],
  },
  {
    id: 9,
    label: 'API-Specific',
    description:
      'Targets API-specific threats, which often bypass traditional WAF rules designed for web applications.',
    ruleGroups: [
      'XSS Injection Semantics Protection Group',
      'JAVA OGNL Semantics Protection',
      'SQL Injection Semantics Protection Group',
      'Command Injection Semantics Protection Group',
    ],
  },
];

/** Look up a managed policy by its vendor `strategy_type` id. */
export function wafPolicyById(id: number): WafPolicy | undefined {
  return WAF_POLICIES.find((p) => p.id === id);
}
